/**
 * Listing Observability Service
 * 
 * Tracks listing view events for monitoring listing page loads and failures.
 * Integrates with NewRelic Browser APM for real-time alerting.
 */

import { trackEvent } from '../utils/event-tracker';
import { LISTING_VIEW_FAILURE, LISTING_SOURCE_RESOLVED } from '../constants/listing-events';

/**
 * Error category for listing view failures
 */
export type ListingErrorCategory = 
  | 'not_found'
  | 'api_error'
  | 'unknown_error';

/**
 * Listing data passed to failure events (will be stringified)
 */
export interface ListingData {
  id: number;
  slug?: string;
  mls?: string;
  address?: string;
}

/**
 * Parameters for tracking a failed listing view.
 */
export interface ListingViewFailureParams {
  /** Listing identification data */
  listingData: ListingData;

  /** The raw error from the backend/UI, or a static error message string */
  error: unknown;

  /** Optional override for error category (auto-detected from error if not provided) */
  errorCategory?: ListingErrorCategory;
}

/**
 * Determines the error category from a raw error object.
 */
function resolveErrorCategory(error: unknown): ListingErrorCategory {
  if (typeof error === 'object' && error !== null) {
    const err = error as { status?: number; statusCode?: number; message?: string };
    if (err.status === 404 || err.statusCode === 404 || (err.message && err.message.includes('not found'))) {
      return 'not_found';
    } else if (err.status || err.statusCode) {
      return 'api_error';
    }
  }
  return 'unknown_error';
}

/**
 * Extracts a human-readable error message from a raw error.
 */
function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    const err = error as { errors?: Array<{ message?: string; title?: string }>; message?: string };

    if (err.errors && Array.isArray(err.errors) && err.errors.length > 0) {
      return err.errors.map(e => e.message || e.title || 'Unknown error').join(', ');
    }

    if (err.message) {
      return err.message;
    }
  }

  return 'Unknown error';
}

/**
 * Listing Observability Service
 * 
 * Pure service with no React dependencies.
 * Called from React components/hooks for tracking.
 */
export const ListingObservabilityService = {
  /**
   * Track failed listing view (when listing fails to load)
   * 
   * Error category is auto-detected from the error object unless overridden.
   * Error message is extracted from the error (string or Error object).
   */
  trackViewFailure(params: ListingViewFailureParams): void {
    const { listingData, error, errorCategory } = params;

    trackEvent(LISTING_VIEW_FAILURE, {
      listingData: JSON.stringify(listingData),
      errorCategory: errorCategory || resolveErrorCategory(error),
      errorMessage: typeof error === 'string' ? error : extractErrorMessage(error),
    });
  },

  /**
   * Track which backend served the listing data (go-search vs client-app fallback).
   * Used to monitor the go-search migration progress on the client side (address modal).
   */
  trackSourceResolved(params: { source: 'go-search' | 'client-app-fallback'; slug: string; count: number }): void {
    trackEvent(LISTING_SOURCE_RESOLVED, params);
  },
} as const;
